import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import api from "../utils/api";
import { DASHBOARD_BLOCKED_MESSAGE } from "../utils/dashboardAccess";
import "./Chatbot.css";

const storageKeys = {
  token: "chatbot_token",
  user: "chatbot_user",
  username: "chatbot_username",
  activeConversation: "chatbot_active_conversation",
};

// Messages fetched when a conversation is opened. Capped because every assistant
// message is re-parsed by renderMarkdown on each render, and this component
// re-renders on every keystroke in the composer — an unbounded transcript would
// make typing progressively laggier.
const TRANSCRIPT_LIMIT = 100;

// Reopen the last conversation on load. Set false to always start fresh.
const RESTORE_LAST_CONVERSATION = true;

const suggestions = [
  {
    label: "Yesterday's Energy",
    prompt: "What was yesterday's energy consumption?"
  },
  {
    label: "High Usage",
    prompt: "Why is my energy consumption high?"
  },
  {
    label: "Peak Demand",
    prompt: "What is the peak energy for my machines?"
  },
  {
    label: "Save Cost",
    prompt: "How can I save money on electricity?"
  },
  {
    label: "Rated Power",
    prompt: "Which machines are not complying with their rated power?"
  },
  {
    label: "Electricity Tariff in Pakistan",
    prompt: "How is electricity tariff calculated in Pakistan?"
  }
];

const loadSession = () => {
  try {
    // Check chatbot-specific session first
    let token = localStorage.getItem(storageKeys.token);
    let userRaw = localStorage.getItem(storageKeys.user);

    // Fallback to main dashboard session if chatbot session is not found
    if (!token || !userRaw) {
      token = localStorage.getItem("token");
      userRaw = localStorage.getItem("user");
    }

    const user = userRaw ? JSON.parse(userRaw) : null;
    if (!token || !user) {
      return null;
    }
    return { token, user };
  } catch {
    return null;
  }
};

// Guards against an unparseable timestamp, which would otherwise render the literal
// string "Invalid Date" in the message footer.
const formatTime = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

// Sidebar timestamp: time for today, weekday within the last week, else a date.
const formatDayLabel = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  const now = new Date();
  if (date.toDateString() === now.toDateString()) return formatTime(value);

  const daysAgo = (now - date) / 86400000;
  if (daysAgo < 7) return date.toLocaleDateString([], { weekday: "short" });
  return date.toLocaleDateString([], { month: "short", day: "numeric" });
};

// localStorage can throw (Safari private mode), so these mirror loadSession's guard.
const loadActiveConversationId = () => {
  try {
    return localStorage.getItem(storageKeys.activeConversation) || null;
  } catch {
    return null;
  }
};

const persistActiveConversationId = (id) => {
  try {
    if (id) localStorage.setItem(storageKeys.activeConversation, id);
    else localStorage.removeItem(storageKeys.activeConversation);
  } catch {
    /* non-fatal: the conversation just won't be restored next load */
  }
};

// Placeholder used only until the server's derived title arrives.
const deriveTitle = (text) => {
  const clean = String(text || "").replace(/\s+/g, " ").trim();
  if (!clean) return "New chat";
  return clean.length > 60 ? `${clean.slice(0, 60)}…` : clean;
};

// Map a stored message onto the entry shape the stream already renders. Role lives
// on the wire; the view keys off `isBot`, so the translation happens here rather
// than in the render tree.
const entryFromHistoryRow = (row, index) => {
  const isBot = row?.role === "assistant";
  return {
    // Server ids give stable React keys. Client-generated ones would change on
    // every render, remounting each row and replaying its entry animation.
    id: row?.message_id != null
      ? `srv-${row.message_id}`
      : `hist-${row?.created_at || "na"}-${index}`,
    // Coerced because renderMarkdown calls .split() and the user branch renders the
    // value directly — a non-string would be a hard crash.
    text: typeof row?.content === "string" ? row.content : String(row?.content ?? ""),
    isBot,
    // Never "thinking": that status renders the typing indicator *instead of* the
    // text, so a mis-mapped historical row would show dots forever.
    status: isBot ? "done" : "saved",
    timestamp: row?.created_at || new Date().toISOString(),
  };
};

const entriesFromHistory = (payload) => {
  const rows = Array.isArray(payload) ? payload : payload?.messages || [];
  return rows.map(entryFromHistoryRow);
};

// Tolerates either a bare array or the documented envelope, and sorts newest-first
// so the sidebar order is right even if the server omits an ORDER BY.
const normalizeConversations = (payload) => {
  const rows = Array.isArray(payload) ? payload : payload?.conversations || [];
  return rows
    .filter((row) => row && row.conversation_id)
    .map((row) => ({
      conversation_id: String(row.conversation_id),
      title: (row.title || "").trim(),
      last_message_at: row.last_message_at || null,
      message_count: Number(row.message_count) || 0,
    }))
    .sort((a, b) => new Date(b.last_message_at || 0) - new Date(a.last_message_at || 0));
};

// Move a conversation to the top of the list after a new message.
const touchConversation = (list, conversationId, patch = {}) => {
  const index = list.findIndex((c) => c.conversation_id === conversationId);
  if (index === -1) return list;

  const updated = {
    ...list[index],
    last_message_at: new Date().toISOString(),
    // Never overwrite a title the server already gave us.
    title: list[index].title || patch.title || "",
  };
  return [updated, ...list.filter((_, i) => i !== index)];
};

function parseInlineMarkdown(text) {
  if (!text) return "";
  const parts = [];
  const boldRegex = /\*\*(.*?)\*\*/g;
  let lastIndex = 0;
  let match;

  while ((match = boldRegex.exec(text)) !== null) {
    const precedingText = text.substring(lastIndex, match.index);
    if (precedingText) {
      parts.push(precedingText);
    }
    parts.push(<strong key={match.index}>{match[1]}</strong>);
    lastIndex = boldRegex.lastIndex;
  }

  const remainingText = text.substring(lastIndex);
  if (remainingText) {
    parts.push(remainingText);
  }

  return parts.length > 0 ? parts : text;
}

function renderMarkdown(text) {
  if (!text) return null;

  const lines = text.split("\n");
  const elements = [];

  let currentList = [];
  let currentListType = null; // "bullet" or "ordered"
  let currentTable = null; // { headers: [], rows: [] }
  let currentParagraph = [];

  const flushParagraph = (key) => {
    if (currentParagraph.length > 0) {
      elements.push(
        <p key={`p-${key}`}>
          {currentParagraph.map((line, idx) => (
            <React.Fragment key={idx}>
              {idx > 0 && <br />}
              {parseInlineMarkdown(line)}
            </React.Fragment>
          ))}
        </p>
      );
      currentParagraph = [];
    }
  };

  const flushList = (key) => {
    if (currentList.length > 0) {
      const Tag = currentListType === "ordered" ? "ol" : "ul";
      elements.push(
        <Tag key={`list-${key}`}>
          {currentList.map((item, idx) => (
            <li key={idx}>{parseInlineMarkdown(item)}</li>
          ))}
        </Tag>
      );
      currentList = [];
      currentListType = null;
    }
  };

  const flushTable = (key) => {
    if (currentTable) {
      elements.push(
        <div className="chatbot-table-wrapper" key={`table-${key}`}>
          <table className="chatbot-table">
            <thead>
              <tr>
                {currentTable.headers.map((col, idx) => (
                  <th key={idx}>{parseInlineMarkdown(col)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {currentTable.rows.map((row, rIdx) => (
                <tr key={rIdx}>
                  {row.map((cell, cIdx) => (
                    <td key={cIdx}>{parseInlineMarkdown(cell)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
      currentTable = null;
    }
  };

  const flushAll = (key) => {
    flushParagraph(key);
    flushList(key);
    flushTable(key);
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();

    // 1. Heading line
    const headingMatch = line.match(/^(#{1,6})\s+(.+)$/);
    if (headingMatch) {
      flushAll(i);
      const level = headingMatch[1].length;
      const Tag = `h${level}`;
      elements.push(<Tag key={`h-${i}`}>{parseInlineMarkdown(headingMatch[2])}</Tag>);
      continue;
    }

    // 2. Table line
    if (line.startsWith("|")) {
      flushParagraph(i);
      flushList(i);

      const cells = line.split("|").slice(1, -1).map(c => c.trim());

      if (!currentTable) {
        // First line of the table (headers)
        currentTable = { headers: cells, rows: [] };
      } else {
        // Is it the delimiter row? e.g. |:---|:---|
        const isDelimiter = cells.every(c => c.replace(/[:-\s]/g, "") === "");
        if (!isDelimiter) {
          currentTable.rows.push(cells);
        }
      }
      continue;
    }

    // 3. Bullet list line
    if (line.startsWith("- ") || line.startsWith("* ")) {
      flushParagraph(i);
      flushTable(i);
      if (currentListType !== "bullet") {
        flushList(i);
        currentListType = "bullet";
      }
      currentList.push(line.substring(2));
      continue;
    }

    // 4. Ordered list line
    const orderedMatch = line.match(/^(\d+)\.\s+(.+)$/);
    if (orderedMatch) {
      flushParagraph(i);
      flushTable(i);
      if (currentListType !== "ordered") {
        flushList(i);
        currentListType = "ordered";
      }
      currentList.push(orderedMatch[2]);
      continue;
    }

    // 5. Empty line (separates blocks)
    if (!line) {
      flushAll(i);
      continue;
    }

    // 6. Regular text line (accumulates into paragraph)
    flushList(i);
    flushTable(i);
    currentParagraph.push(line);
  }

  // Flush any remaining blocks
  flushAll(lines.length);

  return elements;
}

function Chatbot() {
  const [session, setSession] = useState(() => loadSession());
  const [loginForm, setLoginForm] = useState({ username: "", password: "" });
  const [loginError, setLoginError] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [chatEntries, setChatEntries] = useState([]);
  const [sendError, setSendError] = useState("");
  const [sendLoading, setSendLoading] = useState(false);
  const [logoError, setLogoError] = useState(false);

  const [conversations, setConversations] = useState([]);
  const [activeConversationId, setActiveConversationId] = useState(() => loadActiveConversationId());
  const [conversationsLoading, setConversationsLoading] = useState(false);
  const [conversationsError, setConversationsError] = useState("");
  const [transcriptLoading, setTranscriptLoading] = useState(false);
  const [transcriptError, setTranscriptError] = useState("");
  const [srAnnouncement, setSrAnnouncement] = useState("");
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const streamEndRef = useRef(null);
  // Monotonic token so a slow transcript fetch cannot overwrite a newer one.
  const transcriptRequestRef = useRef(0);


  const username = useMemo(() => {
    return localStorage.getItem("username") || localStorage.getItem(storageKeys.username) || session?.user?.username || "demo";
  }, [session]);

  const remoteLogo =
    username && !logoError
      ? `/api/get_logo?username=${username}`
      : null;

  const companyLogo =
    remoteLogo ||
    session?.user?.logo ||
    session?.user?.logoUrl ||
    session?.user?.clientLogo;

  const userInitial = session?.user?.client_name?.charAt(0)?.toUpperCase() || username?.charAt(0)?.toUpperCase() || "U";

  const scrollToBottom = () => {
    streamEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [chatEntries]);

  const authConfig = useMemo(
    () => ({ headers: { Authorization: `Bearer ${session?.token}` } }),
    [session?.token],
  );

  const fetchConversations = useCallback(async () => {
    if (!session?.token) return [];

    setConversationsLoading(true);
    setConversationsError("");
    try {
      const response = await api.get("/chat/conversations", authConfig);
      const list = normalizeConversations(response.data);
      setConversations(list);
      return list;
    } catch (error) {
      // parsedError is attached to every rejection by the interceptor in utils/api.js.
      setConversationsError(
        error?.parsedError?.message || "Could not load your chat history.",
      );
      return [];
    } finally {
      setConversationsLoading(false);
    }
  }, [session?.token, authConfig]);

  const loadConversation = useCallback(async (conversationId) => {
    if (!session?.token || !conversationId) return;

    const requestId = ++transcriptRequestRef.current;

    // Set synchronously so the sidebar highlight tracks the click, not the response.
    setActiveConversationId(conversationId);
    persistActiveConversationId(conversationId);
    setTranscriptError("");
    setSendError("");
    setTranscriptLoading(true);
    setSidebarOpen(false);

    try {
      const response = await api.get(
        `/chat/conversations/${encodeURIComponent(conversationId)}`,
        { ...authConfig, params: { limit: TRANSCRIPT_LIMIT } },
      );
      if (requestId !== transcriptRequestRef.current) return; // superseded
      const entries = entriesFromHistory(response.data);
      setChatEntries(entries);
      setSrAnnouncement(`Loaded conversation with ${entries.length} messages.`);
    } catch (error) {
      if (requestId !== transcriptRequestRef.current) return;
      setChatEntries([]);
      setTranscriptError(
        error?.parsedError?.message || "Could not load this conversation.",
      );
    } finally {
      if (requestId === transcriptRequestRef.current) setTranscriptLoading(false);
    }
  }, [session?.token, authConfig]);

  useEffect(() => {
    if (!session?.token) {
      setConversations([]);
      setActiveConversationId(null);
      setConversationsError("");
      return undefined;
    }

    let ignore = false;
    (async () => {
      const list = await fetchConversations();
      if (ignore) return;

      const stored = loadActiveConversationId();
      if (!stored) return;

      // Validate against the fetched list before restoring: loadSession falls back
      // to the dashboard session, so a stored id can belong to a different client.
      const exists = list.some((c) => c.conversation_id === stored);
      if (exists && RESTORE_LAST_CONVERSATION) {
        loadConversation(stored);
      } else if (!exists) {
        persistActiveConversationId(null);
        setActiveConversationId(null);
      }
    })();

    return () => {
      ignore = true;
    };
  }, [session?.token, fetchConversations, loadConversation]);

  const handleSelectConversation = (conversationId) => {
    if (sendLoading) return;
    // Re-clicking the active conversation would blank and refetch for nothing —
    // unless it failed last time, which makes the click a natural retry.
    if (conversationId === activeConversationId && !transcriptError) return;
    loadConversation(conversationId);
  };

  const handleNewChat = () => {
    if (sendLoading) return;
    // Invalidate any in-flight transcript load, or it would resolve afterwards and
    // repopulate the stream with the conversation we just left.
    transcriptRequestRef.current += 1;
    setActiveConversationId(null);
    persistActiveConversationId(null);
    setChatEntries([]);
    setMessage("");
    setSendError("");
    setTranscriptError("");
    setTranscriptLoading(false);
    setSidebarOpen(false);
    setSrAnnouncement("Started a new chat.");
  };

  const handleLoginChange = (event) => {
    const { name, value } = event.target;
    setLoginForm((current) => ({ ...current, [name]: value }));
  };

  const handleLogin = async (event) => {
    event.preventDefault();
    setLoginError("");
    setLoginLoading(true);

    try {
      const response = await api.post("/signin", {
        username: loginForm.username.trim(),
        password: loginForm.password,
      });
      const user = {
        ...response.data.client,
        token: response.data.token,
      };

      if (user.effective_dashboard_access === false) {
        setLoginError(DASHBOARD_BLOCKED_MESSAGE);
        return;
      }

      localStorage.setItem(storageKeys.token, response.data.token);
      localStorage.setItem(storageKeys.user, JSON.stringify(user));
      localStorage.setItem(storageKeys.username, response.data.client.username || loginForm.username.trim());

      setSession({ token: response.data.token, user });
      setChatEntries([]);
      setMessage("");
    } catch (error) {
      if (error?.response?.status === 403) {
        setLoginError(error?.response?.data?.detail || DASHBOARD_BLOCKED_MESSAGE);
      } else {
        setLoginError("Invalid username or password.");
      }
    } finally {
      setLoginLoading(false);
    }
  };

  // Logging out is the AppShell Header's job — this page no longer has a Logout of
  // its own. Header.handleLogout clears the chatbot_* keys too, including the active
  // conversation id, so the next user of this machine does not inherit a thread.

  const handleSend = async (event) => {
    if (event && typeof event.preventDefault === "function") {
      event.preventDefault();
    }
    const text = message.trim();

    if (!text || !session?.token || sendLoading) {
      return;
    }

    setSendError("");
    setSendLoading(true);

    const pendingEntry = {
      id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
      text,
      status: "saving",
      timestamp: new Date().toISOString(),
    };

    // Append to end of stream
    setChatEntries((current) => [...current, pendingEntry]);

    try {
      // 1. Clear the composer immediately. The user entry stays "saving" until
      //    /chat returns, because /chat is now what persists it — marking it saved
      //    before that would be a lie the moment the request fails.
      setMessage("");

      // 2. Add pending bot entry to the end of stream
      const botPendingId = `${Date.now()}-bot`;
      const botPendingEntry = {
        id: botPendingId,
        text: "Thinking...",
        status: "thinking",
        isBot: true,
        timestamp: new Date().toISOString(),
      };
      setChatEntries((current) => [...current, botPendingEntry]);
      setSrAnnouncement("Ensight is thinking.");

      // 3. Call local Ensight AI agent
      try {
        const aiResponse = await api.post(
          "/chat",
          {
            // username stays in the body: DashboardAccessMiddleware reads it from
            // there to apply the billing block. Identity comes from the token.
            username: username,
            question: text,
            conversation_id: activeConversationId || undefined,
          },
          authConfig,
        );

        // The turn is now persisted server-side, so the question is genuinely
        // saved and the answer can be shown — one pass over the stream for both.
        setChatEntries((current) =>
          current.map((entry) => {
            if (entry.id === pendingEntry.id) {
              return { ...entry, status: "saved" };
            }
            if (entry.id === botPendingId) {
              return { ...entry, text: aiResponse.data.response, status: "done" };
            }
            return entry;
          }),
        );

        // 4. Adopt the conversation the server created, or bump the existing one to
        //    the top of the sidebar. No refetch: it costs a round trip per turn and
        //    can reorder the list under the user's pointer.
        const returnedId = aiResponse.data.conversation_id
          ? String(aiResponse.data.conversation_id)
          : null;
        if (returnedId) {
          const serverTitle = (aiResponse.data.title || "").trim();
          if (!activeConversationId) {
            setActiveConversationId(returnedId);
            persistActiveConversationId(returnedId);
            setConversations((current) => [
              {
                conversation_id: returnedId,
                title: serverTitle || deriveTitle(text),
                last_message_at: new Date().toISOString(),
                message_count: 2,
              },
              ...current.filter((c) => c.conversation_id !== returnedId),
            ]);
          } else {
            setConversations((current) =>
              touchConversation(current, returnedId, { title: serverTitle || deriveTitle(text) }),
            );
          }
        }
      } catch (aiError) {
        console.error("AI chatbot error:", aiError);
        setChatEntries((current) =>
          current.map((entry) =>
            entry.id === botPendingId
              ? {
                  ...entry,
                  text: "Sorry, I could not reach the Ensight AI agent. Please make sure the EnViz API backend is running.",
                  status: "error",
                }
              : entry,
          ),
        );
      }

    } catch (error) {
      setSendError("Could not send the message. Please try again.");
      setChatEntries((current) => current.filter((entry) => entry.id !== pendingEntry.id));
    } finally {
      setSendLoading(false);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleSend();
    }
  };

  // Escape closes the mobile history drawer. It is non-modal (the scrim and
  // selecting a chat also close it), so no focus trap is needed.
  useEffect(() => {
    if (!sidebarOpen) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") setSidebarOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [sidebarOpen]);

  if (!session) {
    return (
      <div className="chatbot-page chatbot-page-signed-in">
        <main className="chatbot-login-layout">
          <section className="chatbot-hero">
            <p className="chatbot-eyebrow">AI Assistant</p>
            <h1>Optimize your energy consumption with Ensight.</h1>
            <p>
              Log in using your enViz credentials to check machine runtimes, discover load shifting savings, and analyze power metrics.
            </p>
          </section>

          <section className="chatbot-card chatbot-login-card">
            <div className="chatbot-card-header">
              <div>
                <h2>Login</h2>
                <p>Enter your credentials to begin.</p>
              </div>
            </div>

            <form className="chatbot-form" onSubmit={handleLogin}>
              <label>
                Username
                <input
                  name="username"
                  value={loginForm.username}
                  onChange={handleLoginChange}
                  placeholder="Username"
                  autoComplete="username"
                />
              </label>

              <label>
                Password
                <input
                  name="password"
                  type="password"
                  value={loginForm.password}
                  onChange={handleLoginChange}
                  placeholder="Password"
                  autoComplete="current-password"
                />
              </label>

              {loginError && <div className="chatbot-error">{loginError}</div>}

              <button type="submit" className="chatbot-primary" disabled={loginLoading}>
                {loginLoading ? "Signing in..." : "Sign in"}
              </button>
            </form>
          </section>
        </main>
      </div>
    );
  }

  return (
    <div className="chatbot-page chatbot-page-signed-in">
      <main className={`chatbot-main ${chatEntries.length === 0 && !transcriptLoading ? 'is-empty' : ''} ${sidebarOpen ? 'sidebar-open' : ''}`}>
        {/* The page deliberately has no chrome of its own: the AppShell Header
            above it already owns the EnViz brand, the client name and Logout, and
            a second bar just repeated all three. Only the drawer toggle survived,
            and it floats over the chat area at the mobile breakpoint. */}
        <button
          type="button"
          className="chatbot-history-toggle"
          onClick={() => setSidebarOpen((open) => !open)}
          aria-expanded={sidebarOpen}
          aria-controls="chatbot-history"
          aria-label="Chat history"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <line x1="3" y1="6" x2="21" y2="6" />
            <line x1="3" y1="12" x2="21" y2="12" />
            <line x1="3" y1="18" x2="21" y2="18" />
          </svg>
        </button>

        <aside
          id="chatbot-history"
          className={`chatbot-history ${sidebarOpen ? "is-open" : ""}`}
          aria-label="Chat history"
        >
          <div className="chatbot-history-header">
            <button
              type="button"
              className="chatbot-new-chat"
              onClick={handleNewChat}
              disabled={sendLoading}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
              New chat
            </button>
          </div>

          {conversationsError && (
            <div className="chatbot-history-error">
              <span>{conversationsError}</span>
              <button type="button" className="chatbot-history-retry" onClick={fetchConversations}>
                Retry
              </button>
            </div>
          )}

          {conversationsLoading && conversations.length === 0 ? (
            <p className="chatbot-history-empty">Loading chats…</p>
          ) : conversations.length === 0 && !conversationsError ? (
            <p className="chatbot-history-empty">No past chats yet.</p>
          ) : (
            <ul className="chatbot-history-list">
              {conversations.map((conversation) => {
                const isActive = conversation.conversation_id === activeConversationId;
                const label = conversation.title || "Untitled chat";
                return (
                  <li key={conversation.conversation_id}>
                    <button
                      type="button"
                      className={`chatbot-history-item ${isActive ? "is-active" : ""}`}
                      onClick={() => handleSelectConversation(conversation.conversation_id)}
                      disabled={sendLoading || transcriptLoading}
                      aria-current={isActive ? "true" : undefined}
                      title={label}
                    >
                      <span className="chatbot-history-item-title">{label}</span>
                      {conversation.last_message_at && (
                        <span className="chatbot-history-item-meta">
                          {formatDayLabel(conversation.last_message_at)}
                        </span>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </aside>

        {sidebarOpen && (
          <div
            className="chatbot-history-scrim"
            onClick={() => setSidebarOpen(false)}
            aria-hidden="true"
          />
        )}

        <section className="chatbot-panel">
          <div className="chatbot-stream" aria-busy={transcriptLoading}>
            {transcriptLoading ? (
              <div className="chatbot-transcript-loading">Loading conversation…</div>
            ) : chatEntries.length === 0 ? (
              <div className="chatbot-empty">
                <h2 className="chatbot-greeting">Ensight</h2>
              </div>
            ) : (
              chatEntries.map((entry) => (
                <article key={entry.id} className={`chatbot-message-row ${entry.isBot ? "bot" : "user"}`}>
                  {entry.isBot && (
                    <div className="chatbot-avatar bot-avatar">
                      <span className="chatbot-brand-primary" style={{ fontWeight: 'bold' }}>En</span>
                    </div>
                  )}
                  <div className={`chatbot-message ${entry.isBot ? "bot" : "user"}`}>
                    <div>
                      {entry.isBot ? (
                        entry.status === "thinking" ? (
                          <div className="chatbot-typing-indicator" aria-label="Thinking">
                            <span className="dot"></span>
                            <span className="dot"></span>
                            <span className="dot"></span>
                          </div>
                        ) : (
                          renderMarkdown(entry.text)
                        )
                      ) : (
                        <p>{entry.text}</p>
                      )}
                    </div>
                    <div className="chatbot-message-footer">
                      <small>{formatTime(entry.timestamp)}</small>
                    </div>
                  </div>
                  {!entry.isBot && (
                    <div className="chatbot-avatar user-avatar">
                      {companyLogo ? (
                        <img
                          src={companyLogo}
                          alt="User"
                          onError={() => setLogoError(true)}
                        />
                      ) : (
                        <span>{userInitial}</span>
                      )}
                    </div>
                  )}
                </article>
              ))
            )}
            <div ref={streamEndRef} />
          </div>

          {sendError && <div className="chatbot-error chatbot-send-error">{sendError}</div>}
          {transcriptError && <div className="chatbot-error chatbot-send-error">{transcriptError}</div>}

          <form className="chatbot-composer" onSubmit={handleSend}>
            <div className="chatbot-suggestions">
              {suggestions.map((s, idx) => (
                <button
                  key={idx}
                  type="button"
                  className="chatbot-suggestion-chip"
                  onClick={() => setMessage(s.prompt)}
                >
                  {s.label}
                </button>
              ))}
            </div>
            <div className="chatbot-composer-input-row" style={{ position: 'relative' }}>
              <textarea
                value={message}
                onChange={(event) => setMessage(event.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Message Ensight..."
                rows={1}
              />
              <button type="submit" className="chatbot-send-button" disabled={sendLoading || !message.trim()}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="19" x2="12" y2="5"></line>
                  <polyline points="5 12 12 5 19 12"></polyline>
                </svg>
              </button>
            </div>
          </form>

          {/* The stream itself is no longer a live region: swapping in a whole
              transcript would queue every message for announcement. Short,
              deliberate sentences go here instead. */}
          <div className="chatbot-sr-only" role="status" aria-live="polite">
            {srAnnouncement}
          </div>
        </section>
      </main>
    </div>
  );
}

export default Chatbot;